/**
 * SQL Investigation section — three-pane analyst-driven SQL editor that
 * lives inside the JIRA tab. Auto-seeds Saved Results cards from the
 * Phase-2 `sql_results` the system already generated, lets the analyst
 * inspect/edit/run SQL freely, and bundles the curated datasets back into
 * the Final Analysis via `POST /issue-analysis/analyze-with-data`.
 *
 * Card state is owned by the parent (IssueAnalysisPanel → AnalysisResult)
 * so EraxTab and TimelineTab can mirror the same live cards.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown, ChevronRight, Play, Plus, Trash2, Edit3, Loader2,
  Send, AlertCircle, Database, Info, X,
} from 'lucide-react';
import { issueAnalysisApi } from '../../services/issueAnalysisApi';
import { formatRowCount } from '../../utils/numberFormat';
import type {
  IssueAnalysisResponse, ModuleTable, TableField, SqlDataResult, ExtractedEntity,
} from '../../services/issueAnalysisApi';
import { SchemaBrowserModal } from '../schema-graph/SchemaBrowserModal';
import { WisdomPanel } from './WisdomPanel';

// ── Card model ──────────────────────────────────────────────────────────────

export type CardKind = 'data' | 'timeline';

export interface SqlCard {
  id: string;
  label: string;
  sql: string;
  rows: Record<string, unknown>[];
  rowCount: number;
  kind: CardKind;
  source: 'auto' | 'manual';
  edited: boolean;
  /** Why the seeder thought this query helps diagnose the ticket
   *  (LLM-authored for stage-2 cards, template for probes). */
  rationale?: string;
  /** Validation/execution failure — shown so a card that didn't run
   *  never looks like a genuine empty result. */
  error?: string;
}

function shortTableName(fqn: string | undefined): string {
  if (!fqn) return '';
  const parts = fqn.split('.');
  return parts[parts.length - 1] || fqn;
}

/** Parse the equality / LIKE conditions out of a generated SELECT's WHERE
 *  clause and render any *beyond the first* in a compact, analyst-readable
 *  form. The first condition is already shown via the entity label
 *  (`entity_type=entity_value`), which the backend fills from the plan's
 *  first entity only (retrieval.rs `execute_plan`). Without this the chip
 *  hides every other condition — a vessel+voyage query showed only
 *  "Vessel=ZHV" while the SQL also filtered "Voyage LIKE '%02614%'".
 *
 *  The Phase-2 query builder (query_planner.rs `build_where_clause`) emits
 *  conditions of the form `col = 'val'` or `col LIKE '%val%'` joined by
 *  ` AND ` in plan-entity order, so the first parsed condition lines up
 *  with the entity label and we drop it here. A tier>2 recency window may
 *  append `col BETWEEN 'a' AND 'b' ORDER BY col DESC`; that clause uses
 *  BETWEEN (not `=` / `LIKE`) so the matcher below skips it. Single-quoted
 *  values may contain doubled `''` escapes (the builder doubles embedded
 *  apostrophes); they are un-doubled for display.
 *
 *  Returns '' when there are no conditions beyond the first. */
function extraWhereConditions(sql: string): string {
  const whereIdx = sql.search(/\bWHERE\b/i);
  if (whereIdx === -1) return '';
  const tail = sql.slice(whereIdx + 'WHERE'.length);
  const re = /([A-Za-z0-9_]+)\s+(=|LIKE)\s+'((?:[^']|'')*)'/gi;
  const parts: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(tail)) !== null) {
    const col = m[1];
    const op = m[2].toUpperCase();
    const val = m[3].replace(/''/g, "'");
    parts.push(op === 'LIKE' ? `${col} like ${val}` : `${col}=${val}`);
  }
  return parts.slice(1).join(' and ');
}

/** Build an analyst-friendly label from an auto-generated sql_result. When
 *  the statement SQL is provided, any WHERE conditions beyond the first
 *  (e.g. the voyage filter in a vessel+voyage query) are appended so the
 *  chip reflects the full predicate, not just the plan's first entity. */
function autoLabel(r: SqlDataResult, sql?: string): string {
  const tbl = shortTableName(r.tables_queried?.[0]);
  const left = tbl || 'query';
  const head = `${r.entity_type}=${r.entity_value}`;
  const extra = sql ? extraWhereConditions(sql) : '';
  return `${left} · ${extra ? `${head} and ${extra}` : head}`;
}

/** Per-statement label using the table parsed out of the FROM clause. */
function labelForStatement(sql: string, fallbackTable: string, r: SqlDataResult): string {
  const tbl = shortTableName(extractTableFromSelect(sql)) || shortTableName(fallbackTable) || 'query';
  const head = `${r.entity_type}=${r.entity_value}`;
  const extra = extraWhereConditions(sql);
  return `${tbl} · ${extra ? `${head} and ${extra}` : head}`;
}

/** Pull the FQN immediately after the first `FROM` in a SELECT. Used to
 *  match each split statement back to its per-table rows in `erax_data`. */
function extractTableFromSelect(sql: string): string | undefined {
  const m = /\bFROM\s+([A-Za-z0-9_.\[\]"]+)/i.exec(sql);
  return m ? m[1].replace(/[\[\]"]/g, '') : undefined;
}

/** Split a SQL blob on `;` while respecting single-quoted string literals
 *  (so `WHERE x = 'A;B'` does NOT split). Trims and drops empty pieces. */
function splitOnSemicolons(sql: string): string[] {
  const out: string[] = [];
  let buf = '';
  let inStr = false;
  for (let i = 0; i < sql.length; i++) {
    const c = sql[i];
    if (inStr) {
      buf += c;
      if (c === "'") {
        // Doubled '' is an escape, stay inside the string.
        if (i + 1 < sql.length && sql[i + 1] === "'") {
          buf += sql[i + 1];
          i++;
        } else {
          inStr = false;
        }
      }
    } else if (c === "'") {
      buf += c;
      inStr = true;
    } else if (c === ';') {
      const trimmed = buf.trim();
      if (trimmed) out.push(trimmed);
      buf = '';
    } else {
      buf += c;
    }
  }
  const tail = buf.trim();
  if (tail) out.push(tail);
  return out;
}

function rowsFrom(eraxData: SqlDataResult['erax_data']): Record<string, unknown>[] {
  if (!eraxData) return [];
  if (Array.isArray(eraxData)) {
    // Per-table format: [{ table, row_count, rows }] — flatten the first
    // table's rows so the card carries displayable rows. Empty arrays stay
    // empty.
    const first = eraxData[0] as Record<string, unknown> | undefined;
    if (first && 'rows' in first && Array.isArray((first as { rows: unknown[] }).rows)) {
      return (first as { rows: Record<string, unknown>[] }).rows;
    }
    return eraxData as Record<string, unknown>[];
  }
  return [];
}

/** Look up per-table rows for a statement: matches the table parsed from
 *  the SQL against `erax_data[*].table` (case-insensitive, suffix match so
 *  the FROM clause's `Cost.dbo.thccarexpensedetail` lines up with the
 *  Phase-2 short name `thccarexpensedetail`). Returns `[]` on no match. */
function rowsForStatement(eraxData: SqlDataResult['erax_data'], sql: string): Record<string, unknown>[] {
  if (!eraxData || !Array.isArray(eraxData)) return [];
  const fqn = (extractTableFromSelect(sql) ?? '').toLowerCase();
  if (!fqn) return [];
  const short = fqn.split('.').pop() ?? fqn;
  for (const entry of eraxData as Array<Record<string, unknown>>) {
    const t = String(entry['table'] ?? '').toLowerCase();
    if (!t) continue;
    if (t === fqn || t.endsWith('.' + short) || t === short) {
      const rows = entry['rows'];
      if (Array.isArray(rows)) return rows as Record<string, unknown>[];
    }
  }
  return [];
}

/** A SqlDataResult produced by the analyst-curated `analyze-with-data`
 *  endpoint (not by Phase 2). Detect with two cheap heuristics:
 *  `entity_type` is exactly the synth marker `data` or `timeline`, AND
 *  `tables_queried` is empty (Phase 2 always populates this with at
 *  least one FQN). When detected, treat `entity_value` as the literal
 *  label the analyst chose, and `entity_type` as the card kind.
 *  Without this carve-out, reloading a curated analysis re-labels every
 *  card as `query · data=<label>` and forces all cards back to
 *  `kind: 'data'` — losing the analyst's labels and turning timeline
 *  cards into data cards. */
function isSyntheticAnalystResult(r: SqlDataResult): boolean {
  return (
    (r.entity_type === 'data' || r.entity_type === 'timeline') &&
    (r.tables_queried?.length ?? 0) === 0
  );
}

/** Seed Saved Results from the auto-generated Phase-2 SQL results, OR
 *  from analyst-curated datasets previously persisted via
 *  `analyze-with-data`.
 *
 *  Phase 2 packs N statements (one per table) into a single
 *  `erax_sql_query` string joined by `;`. LRP executes them as N separate
 *  calls (`query_lrp_with_queries` in retrieval.rs:995). To match that
 *  execution model — and so the analyst can edit / re-run one query at a
 *  time without tripping the single-statement validator — we split each
 *  SqlDataResult into one card per SELECT and pair each statement with
 *  its corresponding per-table rows from `erax_data`.
 *
 *  Synthetic (analyst-curated) results are passed through unchanged —
 *  the SQL is already one statement per record, the rows live directly
 *  on `erax_data` (flat array, not the per-table `[{table, rows}]`
 *  envelope), and the label / kind come from `entity_value` /
 *  `entity_type`. */
export function seedCardsFromResult(result: IssueAnalysisResponse): SqlCard[] {
  const sqlResults = result.retrieval_results?.sql_results ?? [];
  const cards: SqlCard[] = [];
  sqlResults
    .filter((r) => r.erax_sql_query)
    .forEach((r, rIdx) => {
      // Synthetic (analyst-curated, persisted via analyze-with-data):
      // SQL is already one statement, label + kind already chosen by
      // the analyst, rows already a flat array. Pass through verbatim.
      if (isSyntheticAnalystResult(r)) {
        const rows = rowsFrom(r.erax_data);
        cards.push({
          id: `analyst-${rIdx}-${r.entity_value}`,
          label: r.entity_value || 'Untitled query',
          sql: r.erax_sql_query ?? '',
          rows,
          rowCount: rows.length,
          kind: r.entity_type === 'timeline' ? 'timeline' : 'data',
          source: 'auto',
          edited: false,
        });
        return;
      }
      const statements = splitOnSemicolons(r.erax_sql_query ?? '');
      if (statements.length === 0) return;
      if (statements.length === 1) {
        // Single-statement seed — preserve the original label/rows contract.
        // Empty results are kept (with their 0-row badge) so a reopened
        // analysis shows the same cards it had live.
        const rows = rowsFrom(r.erax_data);
        cards.push({
          id: `auto-${rIdx}-${r.entity_type}-${r.entity_value}`,
          label: autoLabel(r, statements[0]),
          sql: statements[0],
          rows,
          rowCount: rows.length,
          kind: 'data',
          source: 'auto',
          edited: false,
        });
        return;
      }
      // Multi-statement: one card per SELECT.
      statements.forEach((stmt, sIdx) => {
        const rows = rowsForStatement(r.erax_data, stmt);
        cards.push({
          id: `auto-${rIdx}-${sIdx}-${r.entity_type}-${r.entity_value}`,
          label: labelForStatement(stmt, r.tables_queried?.[sIdx] ?? '', r),
          sql: stmt,
          rows,
          rowCount: rows.length,
          kind: 'data',
          source: 'auto',
          edited: false,
        });
      });
    });
  return cards;
}

// ── Main component ─────────────────────────────────────────────────────────

interface Props {
  ticketKey: string;
  modules: string[];
  /** Confirmed entities (with their values). Used both to (a) ask the
   *  backend which tables match these entity types, and (b) auto-fill
   *  a WHERE clause when the analyst clicks a column that's known to
   *  match one of these entities. */
  entities: ExtractedEntity[];
  cards: SqlCard[];
  setCards: React.Dispatch<React.SetStateAction<SqlCard[]>>;
  /**
   * When provided, SqlInvestigation uses this list directly instead of
   * fetching its own. Pass the parent's `availableTables` so the
   * Suggested Tables pane stays bit-for-bit identical to the "Tables"
   * panel above. Leave `undefined` for the post-analysis JIRA tab where
   * SqlInvestigation should fetch its own copy.
   */
  availableTables?: ModuleTable[];
  /** Loading flag for the externally-provided `availableTables`. */
  availableTablesLoading?: boolean;
  /** Called when the analyst clicks ⟳ Refresh. When omitted (and
   *  `availableTables` is provided), the Refresh button hides. */
  onRefreshTables?: () => void;
  /**
   * Owner of the "Send ticket + SQL to Analysis" action. Receives the
   * analyst notes string; the cards live in the shared `cards` state the
   * parent already has access to. When `undefined`, the Send button is
   * hidden. Pre-analysis: parent runs intake → skip-Phase-2 analyze →
   * analyze-with-data. Post-analysis: parent calls analyze-with-data
   * directly.
   */
  onSend?: (analystNotes: string) => Promise<void>;
  /** Caption next to the Send button. */
  sendLabel?: string;
  /** Optional title override — used to differentiate the pre- and post-
   *  analysis instances when both render on the same page. */
  title?: string;
  /** Helper sentence shown under the heading. */
  subtitle?: string;
  /** When true, render a "Generating queries…" indicator in the Saved
   *  Results pane. Used by the pre-analysis instance while the LLM seeder
   *  is running. */
  seeding?: boolean;
  /** Inline error to display in the Saved Results pane (e.g. seeder
   *  failure). Cleared by re-trigger. */
  seedingError?: string | null;
  /** Selected tables the deterministic seeder could not probe (no entity
   *  column binding) — shown so exclusions are never silent. */
  seedingSkipped?: { fqn: string; reason: string }[];
  /** Stage 0.5 semantic table suggestions from the seeder (empty unless the
   *  backend toggle is on). High-score ones are auto-included in probing by
   *  the backend (badged); the rest get a one-click add. */
  semanticSuggestions?: { table_fqn: string; matched_field: string; score: number; already_selected: boolean; auto_included: boolean }[];
  /** Stage 0.6 structural table suggestions from the seeder (empty unless
   *  the backend toggle is on). A ticket word resolved against Compass's
   *  real schema catalog and corroborated by a real source sweep — not an
   *  embedding match. Same auto-include/badge contract as Stage 0.5. */
  structuralSuggestions?: { table_fqn: string; matched_term: string; score: number; source_hits: number; already_selected: boolean; auto_included: boolean }[];
  /** Adds a suggested table to the selected set — triggers a re-seed via the
   *  panel's existing debounced effect. */
  onAddTable?: (fqn: string) => void;
  /** Start with the panel body unfolded — used where SQL Investigation IS
   *  the surface the analyst just asked for (the ticket-chat drawer),
   *  rather than a collapsible extra on a crowded page. */
  defaultExpanded?: boolean;
}

export function SqlInvestigation({
  ticketKey, modules, entities,
  cards, setCards,
  availableTables, availableTablesLoading, onRefreshTables,
  onSend, sendLabel, title, subtitle,
  seeding = false, seedingError = null,
  seedingSkipped = [],
  semanticSuggestions = [],
  structuralSuggestions = [],
  onAddTable,
  defaultExpanded = false,
}: Props) {
  const [skippedOpen, setSkippedOpen] = useState(false);
  const [semanticOpen, setSemanticOpen] = useState(false);
  const [structuralOpen, setStructuralOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [sqlText, setSqlText] = useState('');
  const [draftLabel, setDraftLabel] = useState('');
  const [draftKind, setDraftKind] = useState<CardKind>('data');
  const [rowLimit, setRowLimit] = useState<number>(10);
  const [running, setRunning] = useState(false);
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([]);
  const [previewSql, setPreviewSql] = useState<string>('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [analystNotes, setAnalystNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Saved-Results pagination + multi-select. Default to showing the most
  // recent 10 cards so the right pane stays manageable when the seeder
  // produces dozens of auto cards; analyst can flip to "Show all" anytime.
  const [showAllCards, setShowAllCards] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  // Hide 0-row cards in Saved Results (analyst opt-in). Failed cards stay
  // visible even when ON — an error is not a proven-empty result and must
  // never be masked (the 1.53.36 principle).
  const [hideEmptyCards, setHideEmptyCards] = useState(false);

  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  // Reference to the SQL Investigation grid container so the table-detail
  // popover can anchor its RIGHT edge to the grid's right edge — landing
  // on top of the (less important) Saved Results column rather than
  // covering the editor.
  const gridRef = useRef<HTMLDivElement | null>(null);
  // Internal table state — only used when the caller doesn't provide
  // `availableTables`. When the prop IS provided (pre-analysis variant),
  // these stay null/false and the component renders straight from the prop.
  const [internalTables, setInternalTables] = useState<ModuleTable[] | null>(null);
  const [internalTablesLoading, setInternalTablesLoading] = useState(false);
  const [tableFilter, setTableFilter] = useState('');
  // Both default ON: temp tables and 0-row tables are noise for the
  // normal investigation flow — untick to see everything.
  const [hideEmpty, setHideEmpty] = useState(true);
  const [hideTemp, setHideTemp] = useState(true);
  const [openTable, setOpenTable] = useState<string | null>(null);
  // Hover-rich design: clicking a row's ⓘ opens a floating popover that
  // overlays the editor pane with the table's full description, badges,
  // and column list. Click outside / Esc dismisses.
  const [popoverFqn, setPopoverFqn] = useState<string | null>(null);
  // Schema Browser modal — invoked via the "🔍 Browse all" button so the
  // analyst can pick tables from the full LRP schema (not just the
  // ticket's suggested set) and drop them into Saved Results as starter
  // SELECT cards.
  const [browserOpen, setBrowserOpen] = useState(false);
  // Viewport coordinates where the popover should anchor. Needed because
  // the popover lives inside the table pane (which has overflow-y-auto)
  // — `position: absolute` would clip it. We snapshot the clicked
  // button's bounding rect and position the popover with `fixed`.
  const [popoverAnchor, setPopoverAnchor] = useState<{ top: number; left: number } | null>(null);
  const [tableFields, setTableFields] = useState<Record<string, TableField[]>>({});

  const usesExternalTables = availableTables !== undefined;
  const tables: ModuleTable[] | null = usesExternalTables ? availableTables : internalTables;
  const tablesLoading = usesExternalTables ? !!availableTablesLoading : internalTablesLoading;

  // Internal fetch — only when no `availableTables` prop is supplied.
  // Module / entityTypes are serialised into stable string keys to avoid
  // an infinite re-fetch when the caller passes a freshly allocated array
  // on each render.
  // Derive the unique entity-type set for /issue-analysis/tables and for
  // useEffect deps. Stable string keys avoid an infinite re-fetch loop
  // when the parent passes a freshly allocated array each render.
  const entityTypes = useMemo(
    () => Array.from(new Set(entities.map((e) => e.entity_type))),
    [entities],
  );
  const modulesKey = modules.join('|');
  const entityTypesKey = entityTypes.join('|');
  useEffect(() => {
    if (usesExternalTables) return;
    if (!expanded) return;
    let cancelled = false;
    setInternalTables(null);
    setInternalTablesLoading(true);
    issueAnalysisApi.getTablesForModules(modules, entityTypes, ticketKey)
      .then((data) => { if (!cancelled) setInternalTables(data.tables); })
      .catch(() => { if (!cancelled) setInternalTables([]); })
      .finally(() => { if (!cancelled) setInternalTablesLoading(false); });
    return () => { cancelled = true; };
  }, [usesExternalTables, expanded, modulesKey, entityTypesKey, ticketKey]);

  /** Manual refresh — exposed as a small ⟳ button next to the search box so
   *  analysts can force-reload after a backend metadata edit without
   *  collapsing/reopening the section. Delegates to the parent when the
   *  parent owns the tables list (pre-analysis variant). */
  // Close the table-detail popover on Escape.
  useEffect(() => {
    if (!popoverFqn) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPopoverFqn(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popoverFqn]);

  const refreshTables = useCallback(() => {
    if (usesExternalTables) {
      onRefreshTables?.();
      return;
    }
    setInternalTables(null);
    setInternalTablesLoading(true);
    issueAnalysisApi.getTablesForModules(modules, entityTypes, ticketKey)
      .then((data) => setInternalTables(data.tables))
      .catch(() => setInternalTables([]))
      .finally(() => setInternalTablesLoading(false));
  }, [usesExternalTables, onRefreshTables, modules, entityTypes]);

  /** Schema Browser → "Add N cards" callback. For each picked FQN we
   *  append a starter SELECT card to Saved Results, just as if the
   *  analyst had clicked the FQN in the Suggested Tables list. Source
   *  is `manual` because the analyst hand-picked the table (so no AUTO
   *  badge — keeps the audit trail honest). */
  const handleAddTablesFromBrowser = useCallback((fqns: string[]) => {
    if (fqns.length === 0) return;
    const now = Date.now();
    setCards((prev) => [
      ...prev,
      ...fqns.map((fqn, i) => ({
        id: `browser-${now}-${i}-${fqn}`,
        label: shortTableName(fqn),
        sql: `SELECT TOP 5 *\nFROM ${fqn}`,
        rows: [] as Record<string, unknown>[],
        rowCount: 0,
        kind: 'data' as CardKind,
        source: 'manual' as const,
        edited: false,
      })),
    ]);
    setBrowserOpen(false);
  }, [setCards]);

  const filteredTables = useMemo(() => {
    if (!tables) return [];
    const q = tableFilter.trim().toLowerCase();
    const matched = q
      ? tables.filter((t) => t.fqn.toLowerCase().includes(q) || t.name.toLowerCase().includes(q))
      : tables;
    const afterEmpty = hideEmpty
      ? matched.filter((t) => t.has_data !== false)
      : matched;
    const afterTemp = hideTemp
      ? afterEmpty.filter((t) => {
          const name = t.name.toLowerCase();
          return !name.includes('temp') && !name.includes('tmp');
        })
      : afterEmpty;
    // Sort alphabetically by short table name (case-insensitive). Easier
    // to scan than the module-grouped order returned by the backend.
    return afterTemp.slice().sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [tables, tableFilter, hideEmpty, hideTemp]);

  const handleToggleTable = useCallback((fqn: string) => {
    setOpenTable((cur) => (cur === fqn ? null : fqn));
    if (!tableFields[fqn]) {
      issueAnalysisApi.getTableFields(fqn)
        .then((d) => setTableFields((prev) => ({ ...prev, [fqn]: d.fields })))
        .catch(() => {/* leave undefined; analyst can retry by closing/reopening */});
    }
  }, [tableFields]);

  /** Click on a suggested table → start a NEW query with a starter
   *  `SELECT TOP 100 * FROM <fqn>` so analysts new to SQL have a runnable
   *  query immediately. Picking a table is treated as "begin a fresh
   *  query": the previously active card is deselected (so the next Save
   *  creates a new card rather than overwriting), the label is replaced
   *  with the new table name, and any old preview is cleared. If the
   *  editor already has user-typed SQL that isn't a starter snippet, the
   *  analyst is asked to confirm before the overwrite. */
  const handlePickTable = useCallback((fqn: string, name: string) => {
    const starter = `SELECT TOP 5 *\nFROM ${fqn}`;
    const isStarterSnippet = /^SELECT\s+TOP\s+\d+\s+\*\s+FROM\s+\S+\s*$/i.test(sqlText.trim());
    const overwrite =
      sqlText.trim() === ''
      || isStarterSnippet
      || window.confirm('Replace the current SQL with a starter SELECT for this table?');
    if (!overwrite) return;

    // Treat as a brand-new query so Save creates a fresh card.
    setActiveCardId(null);
    setSqlText(starter);
    setDraftLabel(name);
    setDraftKind('data');
    setPreviewRows([]);
    setPreviewSql('');
    setPreviewError(null);

    // Expand the column list so the analyst can refine columns next.
    setOpenTable(fqn);
    if (!tableFields[fqn]) {
      issueAnalysisApi.getTableFields(fqn)
        .then((d) => setTableFields((prev) => ({ ...prev, [fqn]: d.fields })))
        .catch(() => {});
    }
    requestAnimationFrame(() => {
      if (editorRef.current) {
        editorRef.current.focus();
        const pos = starter.length;
        editorRef.current.selectionStart = pos;
        editorRef.current.selectionEnd = pos;
      }
    });
  }, [sqlText, tableFields]);

  // Insert a token at the textarea's current cursor position. Falls back to
  // appending with a space if the ref isn't ready.
  const insertAtCursor = useCallback((token: string) => {
    const ta = editorRef.current;
    if (!ta) {
      setSqlText((s) => (s ? `${s} ${token}` : token));
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    setSqlText((s) => `${s.slice(0, start)}${token}${s.slice(end)}`);
    requestAnimationFrame(() => {
      if (editorRef.current) {
        const pos = start + token.length;
        editorRef.current.selectionStart = pos;
        editorRef.current.selectionEnd = pos;
        editorRef.current.focus();
      }
    });
  }, []);

  /** Smart field insertion from the popover — keeps the SELECT statement
   *  and the field's owning table in sync, and adds a WHERE clause when
   *  the clicked column maps to a known ticket entity.
   *
   *  Behaviour:
   *  - Empty editor:
   *      • with matched entity value → `SELECT TOP 5 * FROM <fqn>
   *        WHERE <field> = '<value>'`
   *      • without → `SELECT TOP 5 <field> FROM <fqn>`
   *  - Starter snippet (`SELECT TOP N * FROM <tbl>`):
   *      • same table + matched value → append `WHERE <field> = '<value>'`
   *        (keep the * — analyst wanted to filter, not project).
   *      • same table + no matched value → replace `*` with `<field>`.
   *      • different table → confirm switching to <fqn>; on yes, reseed
   *        with a smart query for the new table.
   *  - Anything else → fall back to bare-name insertion at the cursor.
   *
   *  The matched entity value comes from `table.entity_matches` — when
   *  one of its `matched_field` entries equals `fieldName` (case-
   *  insensitive), look up the first entity in `entities` whose
   *  `entity_type` matches and use its `value`. Values are escaped by
   *  doubling any single quote.
   */
  const insertField = useCallback((
    fieldName: string,
    fqn: string,
    tableEntityMatches?: { entity_type: string; matched_field: string }[],
  ) => {
    // Look up a matched value for this field, if any.
    const match = tableEntityMatches?.find(
      (m) => m.matched_field.toLowerCase() === fieldName.toLowerCase(),
    );
    const matchedEntity = match
      ? entities.find((e) => e.entity_type === match.entity_type)
      : undefined;
    const matchedValue = matchedEntity?.value?.trim() || undefined;
    const escapedValue = matchedValue?.replace(/'/g, "''");

    const buildSmart = () => matchedValue
      ? `SELECT TOP 5 *\nFROM ${fqn}\nWHERE ${fieldName} = '${escapedValue}'`
      : `SELECT TOP 5 ${fieldName}\nFROM ${fqn}`;

    const trimmed = sqlText.trim();
    if (!trimmed) {
      setSqlText(buildSmart());
      setDraftLabel(shortTableName(fqn));
      return;
    }
    const starterMatch = /^SELECT\s+TOP\s+\d+\s+\*\s+FROM\s+(\S+)\s*$/i.exec(trimmed);
    if (starterMatch) {
      const currentTable = starterMatch[1];
      const sameTable = currentTable.toLowerCase() === fqn.toLowerCase();
      if (sameTable) {
        if (matchedValue) {
          // Append a WHERE clause to the starter — keep the * because
          // the analyst wants to filter rows, not project columns.
          setSqlText(`${trimmed}\nWHERE ${fieldName} = '${escapedValue}'`);
        } else {
          setSqlText(sqlText.replace('*', fieldName));
        }
        return;
      }
      const switchOk = window.confirm(
        `Editor currently SELECTs from ${currentTable}.\nSwitch to ${fqn} and use ${fieldName}${matchedValue ? ` with WHERE = '${matchedValue}'` : ''}?`
      );
      if (switchOk) {
        setSqlText(buildSmart());
        setDraftLabel(shortTableName(fqn));
      }
      return;
    }
    // Fallback: bare-name insertion at the cursor.
    insertAtCursor(fieldName);
  }, [sqlText, entities, insertAtCursor]);

  // ── Run / save / load ────────────────────────────────────────────────────

  /** AbortController for the in-flight run-sql request. Clicking Run a
   *  second time aborts the previous request so its response cannot
   *  race-overwrite the new one. Also cleared on unmount. */
  const runAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => runAbortRef.current?.abort(), []);

  const handleRun = useCallback(async () => {
    runAbortRef.current?.abort();
    const controller = new AbortController();
    runAbortRef.current = controller;
    setRunning(true);
    setPreviewError(null);
    try {
      const res = await issueAnalysisApi.runSql({
        ticket_key: ticketKey,
        sql: sqlText,
        row_limit: rowLimit,
      }, controller.signal);
      if (controller.signal.aborted) return;
      if (res.error) {
        setPreviewError(res.error);
        setPreviewRows([]);
        setPreviewSql(sqlText);  // Keep edited query, not backend-wrapped version
      } else {
        setPreviewRows(res.rows);
        setPreviewSql(sqlText);  // Keep edited query, not backend-wrapped version
      }
    } catch (e) {
      // AbortError from a fast re-click is not a user-facing failure —
      // the newer request will display its own state. Silently swallow.
      if (controller.signal.aborted) return;
      const name = (e as { name?: string })?.name;
      if (name === 'AbortError') return;
      setPreviewError(e instanceof Error ? e.message : String(e));
      setPreviewRows([]);
    } finally {
      if (!controller.signal.aborted) setRunning(false);
    }
  }, [ticketKey, sqlText, rowLimit]);

  const handleSave = useCallback(() => {
    const label = draftLabel.trim() || 'Untitled query';
    if (activeCardId) {
      // Overwrite the active card in place — applies to auto and manual alike.
      // error reflects the LATEST run: a repaired card sheds its stale
      // failure; an edit that now fails is saved as failed, not "empty".
      setCards((prev) => prev.map((c) => c.id === activeCardId ? {
        ...c,
        label,
        sql: sqlText,
        rows: previewRows,
        rowCount: previewRows.length,
        kind: draftKind,
        edited: c.source === 'auto' ? true : c.edited,
        error: previewError ?? undefined,
      } : c));
    } else {
      const id = `manual-${Date.now()}`;
      setCards((prev) => [...prev, {
        id,
        label,
        sql: sqlText,
        rows: previewRows,
        rowCount: previewRows.length,
        kind: draftKind,
        source: 'manual',
        edited: false,
      }]);
      setActiveCardId(id);
    }
  }, [draftLabel, sqlText, previewRows, draftKind, activeCardId]);

  const handleLoadCard = useCallback((card: SqlCard) => {
    setActiveCardId(card.id);
    setSqlText(card.sql);
    setDraftLabel(card.label);
    setDraftKind(card.kind);
    setPreviewRows(card.rows);
    setPreviewSql(card.error ? '' : card.sql);
    setPreviewError(card.error ?? null);
  }, []);

  const handleNew = useCallback(() => {
    setActiveCardId(null);
    setSqlText('');
    setDraftLabel('');
    setDraftKind('data');
    setPreviewRows([]);
    setPreviewSql('');
    setPreviewError(null);
    editorRef.current?.focus();
  }, []);

  const handleDeleteCard = useCallback((id: string) => {
    setCards((prev) => prev.filter((c) => c.id !== id));
    setSelectedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    if (activeCardId === id) handleNew();
  }, [activeCardId, handleNew]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAllVisible = useCallback((ids: string[], select: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (select) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const handleDeleteSelected = useCallback(() => {
    setSelectedIds((selected) => {
      if (selected.size === 0) return selected;
      setCards((prev) => prev.filter((c) => !selected.has(c.id)));
      if (activeCardId && selected.has(activeCardId)) handleNew();
      return new Set();
    });
  }, [activeCardId, handleNew, setCards]);

  // ── Send to Analysis ─────────────────────────────────────────────────────

  const canSend = cards.length > 0 && onSend != null;

  const handleSend = useCallback(async () => {
    if (!onSend) return;
    setSending(true);
    setSendError(null);
    try {
      await onSend(analystNotes.trim());
    } catch (e) {
      setSendError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }, [onSend, analystNotes]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="mt-4 border-t border-gray-200 pt-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-left group"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Database size={14} className="text-indigo-600" />
          <span className="text-sm font-semibold text-gray-800 group-hover:text-indigo-700">
            {title ?? 'SQL Investigation'}
          </span>
          <span className="text-[10px] text-gray-500">
            ({cards.length} saved{cards.some((c) => c.source === 'auto' && !c.edited) ? ' · seeded' : ''})
          </span>
          {seeding && (
            <span className="flex items-center gap-1 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">
              <Loader2 size={10} className="animate-spin" />
              SQL probe running, please wait…
            </span>
          )}
        </div>
      </button>
      {expanded && subtitle && (
        <p className="mt-1 text-[11px] text-gray-500 italic">{subtitle}</p>
      )}

      {expanded && (
        <div ref={gridRef} className="mt-3 grid grid-cols-12 gap-3">
          {/* Left — Tables */}
          <div className="col-span-3 border border-gray-200 rounded p-2 max-h-[520px] overflow-y-auto relative">
            <div className="mb-1">
              <div className="flex items-center justify-between gap-1">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                  Suggested tables
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setBrowserOpen(true)}
                    className="text-[10px] text-indigo-600 hover:text-indigo-800"
                    title="Browse the full schema and add tables as starter cards"
                  >
                    🔍 Browse all
                  </button>
                  {(!usesExternalTables || onRefreshTables) && (
                    <button
                      type="button"
                      onClick={refreshTables}
                      disabled={tablesLoading}
                      className="text-[10px] text-indigo-600 hover:text-indigo-800 disabled:opacity-40"
                      title="Refresh table list from server"
                    >
                      {tablesLoading ? <Loader2 size={10} className="animate-spin inline" /> : '⟳ Refresh'}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] text-gray-600 hover:text-gray-900">
                  <input
                    type="checkbox"
                    checked={hideTemp}
                    onChange={(e) => setHideTemp(e.target.checked)}
                    className="h-3 w-3"
                    title="Hide tables with temp/tmp in the name"
                  />
                  <span>Hide temp</span>
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] text-gray-600 hover:text-gray-900">
                  <input
                    type="checkbox"
                    checked={hideEmpty}
                    onChange={(e) => setHideEmpty(e.target.checked)}
                    className="h-3 w-3"
                    title="Hide tables with no data"
                  />
                  <span>Hide empty</span>
                </label>
              </div>
            </div>
            <input
              type="text"
              placeholder="Search…"
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              className="w-full text-[11px] border border-gray-300 rounded px-1.5 py-0.5 mb-2"
            />
            {tablesLoading && (
              <div className="flex items-center gap-1 text-[10px] text-gray-500">
                <Loader2 size={10} className="animate-spin" /> Loading…
              </div>
            )}
            {!tablesLoading && tables && filteredTables.length === 0 && (
              <p className="text-[10px] italic text-gray-400">No tables match.</p>
            )}
            <ul className="space-y-0.5">
              {filteredTables.map((t) => {
                const matchCount = (t.entity_matches ?? []).length;
                return (
                  <li
                    key={t.fqn}
                    className={`flex items-center gap-1 px-1 py-0.5 rounded hover:bg-gray-50 ${t.excluded ? 'opacity-60' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => handlePickTable(t.fqn, t.name)}
                      className="flex-1 min-w-0 text-left text-[11px] font-mono text-indigo-700 hover:text-indigo-900 break-words"
                      style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                      title={`Click to seed SELECT TOP 5 * FROM ${t.fqn}`}
                    >
                      {t.name}
                      {t.module && (
                        <span className="text-gray-400 font-sans ml-1">({t.module})</span>
                      )}
                    </button>
                    {/* Frequency tier badge — shows probability of fix being in this table. */}
                    {t.frequency_tier && (
                      <span
                        className={`flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                          t.frequency_tier === 'high'
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                            : t.frequency_tier === 'rare'
                              ? 'bg-gray-100 text-gray-600 border border-gray-300'
                              : 'bg-slate-50 text-slate-500 border border-slate-200'
                        }`}
                        title={`Historical frequency: ${t.frequency_tier}. Tables marked "high" are commonly involved in fixes for this module.${t.freq_synced_at ? ` Last updated: ${new Date(t.freq_synced_at).toLocaleDateString()}` : ''}`}
                      >
                        {t.frequency_tier === 'unknown' ? 'Unk' : t.frequency_tier}
                      </span>
                    )}

                    {/* Row count badge — shows data availability. */}
                    {t.row_count !== undefined && (
                      <span
                        className={`flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                          t.has_data
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-amber-100 text-amber-700 border border-amber-300'
                        }`}
                        title={t.has_data ? `${t.row_count.toLocaleString()} rows available` : 'No data in this table'}
                      >
                        {t.has_data ? `${formatRowCount(t.row_count)} rows` : 'empty'}
                      </span>
                    )}

                    {/* Match-count dots — at-a-glance signal of relevance. */}
                    {matchCount > 0 && (
                      <span
                        className="flex items-center gap-0.5 flex-shrink-0"
                        title={`${matchCount} column match${matchCount === 1 ? '' : 'es'} for the ticket entities`}
                        aria-label={`${matchCount} entity matches`}
                      >
                        {Array.from({ length: Math.min(matchCount, 4) }).map((_, i) => (
                          <span key={i} className="w-1 h-1 rounded-full bg-purple-500" />
                        ))}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        // Anchor popover to the GRID's right edge (over
                        // Saved Results), not to the Info icon's right
                        // edge (which would cover the editor). Vertically
                        // align with the clicked row but clamp to keep
                        // the popover fully on screen.
                        const POPOVER_WIDTH = 360;
                        const POPOVER_HEIGHT = 520;
                        const MARGIN = 16;
                        const iconRect = e.currentTarget.getBoundingClientRect();
                        const gridRect = gridRef.current?.getBoundingClientRect();
                        const rightEdge = gridRect ? gridRect.right : window.innerWidth - MARGIN;
                        const desiredLeft = rightEdge - POPOVER_WIDTH;
                        const desiredTop = iconRect.top;
                        const maxTop = window.innerHeight - POPOVER_HEIGHT - MARGIN;
                        setPopoverAnchor({
                          top: Math.max(MARGIN, Math.min(desiredTop, maxTop)),
                          left: Math.max(MARGIN, desiredLeft),
                        });
                        setPopoverFqn(t.fqn);
                        if (!tableFields[t.fqn]) {
                          issueAnalysisApi.getTableFields(t.fqn)
                            .then((d) => setTableFields((prev) => ({ ...prev, [t.fqn]: d.fields })))
                            .catch(() => {});
                        }
                      }}
                      className="flex-shrink-0 text-gray-400 hover:text-indigo-600"
                      aria-label={`Details for ${t.name}`}
                      title="View description, badges, and columns"
                    >
                      <Info size={11} />
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Transparent backdrop — clicking anywhere outside the
                popover closes it. Pointer-events let clicks through any
                content underneath (the popover itself stops propagation). */}
            {popoverFqn && (
              <div
                className="fixed inset-0 z-20"
                onClick={() => setPopoverFqn(null)}
                aria-hidden
              />
            )}

            {/* Floating detail popover — overlays the editor pane to the
                right of the table list. Dismissed by clicking the × or
                anywhere outside, or pressing Escape. */}
            {popoverFqn && (() => {
              const t = (tables ?? []).find((x) => x.fqn === popoverFqn);
              if (!t) return null;
              const entityMatches = t.entity_matches ?? [];
              const matchByField = new Map<string, string>();
              for (const em of entityMatches) {
                matchByField.set(em.matched_field.toLowerCase(), em.entity_type);
              }
              const fields = tableFields[t.fqn];
              return (
                <div
                  className="fixed w-[360px] max-h-[520px] overflow-y-auto bg-white shadow-xl border border-gray-300 rounded-lg p-3 z-30"
                  style={popoverAnchor ? { top: popoverAnchor.top, left: popoverAnchor.left } : undefined}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => { handlePickTable(t.fqn, t.name); setPopoverFqn(null); }}
                        className="text-sm font-mono font-semibold text-indigo-700 hover:bg-indigo-50 px-1 rounded"
                        title={`Insert SELECT TOP 5 * FROM ${t.fqn}`}
                      >
                        {t.name}
                      </button>
                      {t.module && (
                        <span className="text-[11px] text-gray-500 ml-1">({t.module})</span>
                      )}
                      {t.excluded && (
                        <span className="text-[9px] px-1 py-0 ml-1 bg-gray-200 text-gray-600 rounded">excluded</span>
                      )}
                      <div className="text-[10px] font-mono text-gray-400 mt-0.5 truncate">{t.fqn}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPopoverFqn(null)}
                      className="text-gray-400 hover:text-gray-700 flex-shrink-0"
                      aria-label="Close details"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {entityMatches.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {entityMatches.map((em) => (
                        <span
                          key={`${t.fqn}-pop-em-${em.entity_type}`}
                          className="px-1.5 py-0.5 text-[10px] bg-purple-100 text-purple-700 rounded border border-purple-200"
                          title={`Column "${em.matched_field}" matches ${em.entity_type}`}
                        >
                          {em.entity_type}: {em.matched_field}
                        </span>
                      ))}
                    </div>
                  )}

                  {t.description && (
                    <p className="text-[11px] text-gray-700 break-words whitespace-normal mb-2 leading-snug">
                      {t.description}
                    </p>
                  )}

                  <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Columns
                    {fields && (
                      <span className="font-normal text-gray-400 ml-1">({fields.length})</span>
                    )}
                  </div>

                  {!fields && (
                    <div className="flex items-center gap-1 text-[10px] text-gray-500">
                      <Loader2 size={10} className="animate-spin" /> Loading columns…
                    </div>
                  )}
                  {fields && fields.length === 0 && (
                    <p className="text-[10px] italic text-gray-400">No columns reported for this table.</p>
                  )}
                  {fields && fields.length > 0 && (
                    <ul className="space-y-1.5">
                      {fields.map((f) => {
                        const match = matchByField.get(f.name.toLowerCase());
                        return (
                          <li key={`pop-${f.name}`} className="leading-snug">
                            <div className="flex items-baseline gap-1.5 flex-wrap">
                              <button
                                type="button"
                                onClick={() => { insertField(f.name, t.fqn, t.entity_matches); }}
                                className="text-[11px] font-mono font-medium text-gray-900 hover:bg-indigo-50 px-1 rounded"
                                title={`Use ${f.name} in a SELECT against ${t.fqn}`}
                              >
                                {f.name}
                              </button>
                              {f.data_type && (
                                <span className="text-[10px] text-gray-400">{f.data_type}</span>
                              )}
                              {match && (
                                <span
                                  className="px-1 py-0 text-[9px] bg-purple-100 text-purple-700 rounded border border-purple-200"
                                  title={`Matches ticket entity ${match}`}
                                >
                                  {match}
                                </span>
                              )}
                            </div>
                            {f.description && (
                              <div className="text-[10px] text-gray-500 break-words whitespace-normal ml-1 mt-0.5">
                                {f.description}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Centre — Editor */}
          <div className="col-span-6 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                  {activeCardId ? 'Editing card' : 'New query'}
                </span>
                {activeCardId && (
                  <button
                    type="button"
                    onClick={handleNew}
                    className="text-[10px] text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-0.5"
                  >
                    <Plus size={10} /> New
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] text-gray-500">Cap</label>
                <select
                  value={rowLimit}
                  onChange={(e) => setRowLimit(Number(e.target.value))}
                  className="text-[10px] border border-gray-300 rounded px-1 py-0.5"
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
                <button
                  type="button"
                  onClick={handleRun}
                  disabled={running || !sqlText.trim()}
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-40"
                >
                  {running ? <Loader2 size={10} className="animate-spin" /> : <Play size={10} />} Run
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!previewSql && !sqlText.trim()}
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40"
                  title={activeCardId ? 'Overwrite this card with the current editor contents' : 'Save as a new card'}
                >
                  {activeCardId ? <Edit3 size={10} /> : <Plus size={10} />} Save
                </button>
              </div>
            </div>

            <textarea
              ref={editorRef}
              value={sqlText}
              onChange={(e) => setSqlText(e.target.value)}
              placeholder="SELECT TOP 5 *&#10;FROM Documentation.dbo.bl_cargo&#10;WHERE eqpid = 'MAEU1234567'"
              className="w-full h-44 font-mono text-[11px] border border-gray-300 rounded p-2 resize-y"
              spellCheck={false}
            />

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                placeholder="Label, e.g. bl_cargo for MAEU1234567"
                className="flex-1 text-[11px] border border-gray-300 rounded px-2 py-1"
              />
              <select
                value={draftKind}
                onChange={(e) => setDraftKind(e.target.value as CardKind)}
                className="text-[11px] border border-gray-300 rounded px-1 py-1"
                title="Saved data shows in ERAX tab. Timeline saved data shows in Event Timeline tab."
              >
                <option value="data">data</option>
                <option value="timeline">timeline</option>
              </select>
            </div>

            {/* Results preview */}
            <div className="border-t border-gray-200 pt-2">
              {previewError && (
                <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-1.5">
                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{previewError}</span>
                </div>
              )}
              {!previewError && previewSql && (
                <>
                  <div className="text-[10px] text-gray-500 mb-1">
                    {previewRows.length} row{previewRows.length === 1 ? '' : 's'} · executed:{' '}
                    <code className="font-mono text-gray-700">{previewSql}</code>
                  </div>
                  {previewRows.length === 0 ? (
                    <p className="text-[11px] text-gray-500 italic">No rows returned.</p>
                  ) : (
                    <RowsTable rows={previewRows} />
                  )}
                </>
              )}
              {/* Wisdom capture — after any run, success or failure */}
              {(previewSql || previewError) && (
                <WisdomPanel sql={previewSql || sqlText} rows={previewRows} />
              )}
            </div>
          </div>

          {/* Right — Saved Results */}
          <div className="col-span-3 border border-gray-200 rounded p-2 max-h-[520px] overflow-y-auto flex flex-col gap-2">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <span>Saved results ({cards.length})</span>
              <button
                onClick={() => setInfoOpen((v) => !v)}
                className={`shrink-0 ${infoOpen ? 'text-indigo-500' : 'text-gray-400'} hover:text-indigo-500`}
                title="How queries are seeded (Entity Types)"
              >
                <Info size={11} />
              </button>
              {seeding && <Loader2 size={10} className="animate-spin text-indigo-500" />}
            </div>
            {infoOpen && (
              <div className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 rounded p-1.5 flex items-start gap-1">
                <Info size={11} className="mt-0.5 shrink-0 text-gray-400" />
                <span>
                  Queries bind entity values to table columns via <strong>Entity Types</strong> (Doc
                  Processing → Entity Types). Definitions are re-read from the database on every
                  run — fix a column candidate or add a type, re-run the ticket, and the change
                  applies immediately (no restart).
                </span>
              </div>
            )}
            {seeding && (
              <div className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-1.5 flex items-start gap-1">
                <Loader2 size={11} className="animate-spin mt-0.5 shrink-0" />
                <span>
                  Generating compound SELECTs with LLM — matching entity values to
                  table columns by field description. This usually takes 20–60s.
                </span>
              </div>
            )}
            {seedingError && !seeding && (
              <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded p-1.5">
                Auto-seed failed: {seedingError}
              </div>
            )}
            {seedingSkipped.length > 0 && !seeding && (
              <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5">
                <button
                  onClick={() => setSkippedOpen((v) => !v)}
                  className="font-medium underline decoration-dotted"
                  title="No confirmed entity binds to a column in these tables"
                >
                  {seedingSkipped.length} selected table{seedingSkipped.length === 1 ? '' : 's'} not auto-probed
                </button>
                {skippedOpen && (
                  <ul className="mt-1 space-y-0.5 font-mono">
                    {seedingSkipped.map((s) => (
                      <li key={s.fqn} title={s.reason}>{s.fqn}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {!seeding && (() => {
              const fresh = semanticSuggestions.filter((s) => !s.already_selected);
              if (fresh.length === 0) return null;
              const autoCount = fresh.filter((s) => s.auto_included).length;
              return (
                <div className="text-[10px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded p-1.5">
                  <button
                    onClick={() => setSemanticOpen((v) => !v)}
                    className="font-medium underline decoration-dotted"
                  >
                    {fresh.length} semantically related table{fresh.length === 1 ? '' : 's'} found (Stage 0.5)
                  </button>
                  {autoCount > 0 && (
                    <>
                      {' '}— <span className="font-medium">{autoCount} auto-included in probing</span>
                    </>
                  )}
                  {semanticOpen && (
                    <ul className="mt-1 space-y-0.5 font-mono">
                      {fresh.map((s) => (
                        <li
                          key={s.table_fqn}
                          title={`best-matching field: ${s.matched_field}`}
                          className="flex items-center gap-1"
                        >
                          <span>
                            {s.table_fqn}{' '}
                            <span className="text-indigo-500">
                              ({s.matched_field} · sim {s.score.toFixed(2)})
                            </span>
                          </span>
                          {s.auto_included ? (
                            <span className="px-1 rounded bg-indigo-200 text-indigo-900 text-[9px]">
                              auto
                            </span>
                          ) : (
                            onAddTable && (
                              <button
                                onClick={() => onAddTable(s.table_fqn)}
                                className="px-1 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-100 text-[9px]"
                                title="Add to selected tables and re-seed"
                              >
                                + add
                              </button>
                            )
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}
            {!seeding && (() => {
              const fresh = structuralSuggestions.filter((s) => !s.already_selected);
              if (fresh.length === 0) return null;
              const autoCount = fresh.filter((s) => s.auto_included).length;
              return (
                <div className="text-[10px] text-teal-800 bg-teal-50 border border-teal-200 rounded p-1.5">
                  <button
                    onClick={() => setStructuralOpen((v) => !v)}
                    className="font-medium underline decoration-dotted"
                  >
                    {fresh.length} table{fresh.length === 1 ? '' : 's'} found in real code/schema (Stage 0.6)
                  </button>
                  {autoCount > 0 && (
                    <>
                      {' '}— <span className="font-medium">{autoCount} auto-included in probing</span>
                    </>
                  )}
                  {structuralOpen && (
                    <ul className="mt-1 space-y-0.5 font-mono">
                      {fresh.map((s) => (
                        <li
                          key={s.table_fqn}
                          title={`matched ticket word "${s.matched_term}"${s.source_hits > 0 ? `, seen in ${s.source_hits} real procedure(s)` : ''}`}
                          className="flex items-center gap-1"
                        >
                          <span>
                            {s.table_fqn}{' '}
                            <span className="text-teal-600">
                              ({s.matched_term} · {s.score.toFixed(2)}
                              {s.source_hits > 0 ? `, ${s.source_hits} proc${s.source_hits === 1 ? '' : 's'}` : ''})
                            </span>
                          </span>
                          {s.auto_included ? (
                            <span className="px-1 rounded bg-teal-200 text-teal-900 text-[9px]">
                              auto
                            </span>
                          ) : (
                            onAddTable && (
                              <button
                                onClick={() => onAddTable(s.table_fqn)}
                                className="px-1 rounded border border-teal-300 text-teal-700 hover:bg-teal-100 text-[9px]"
                                title="Add to selected tables and re-seed"
                              >
                                + add
                              </button>
                            )
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}
            {cards.length === 0 && !seeding && (
              <p className="text-[10px] italic text-gray-400">
                {seedingError
                  ? 'Run a query and click Save to add a card here.'
                  : 'Waiting for entities, modules and tables…'}
              </p>
            )}
            {cards.length > 0 && (() => {
              const listedCards = hideEmptyCards
                ? cards.filter((c) => c.rowCount > 0 || c.error)
                : cards;
              const hiddenCount = cards.length - listedCards.length;
              const visibleCards = showAllCards ? listedCards : listedCards.slice(0, 10);
              const visibleIds = visibleCards.map((c) => c.id);
              const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
              return (
                <>
                  <div className="flex items-center justify-between gap-1 text-[10px] text-gray-600 flex-wrap">
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={(e) => handleSelectAllVisible(visibleIds, e.target.checked)}
                        className="h-3 w-3"
                      />
                      <span>Select all{showAllCards ? '' : ' (top 10)'}</span>
                    </label>
                    <label
                      className="inline-flex items-center gap-1 cursor-pointer"
                      title="Hide cards whose query returned 0 rows. Failed cards stay visible."
                    >
                      <input
                        type="checkbox"
                        checked={hideEmptyCards}
                        onChange={(e) => setHideEmptyCards(e.target.checked)}
                        className="h-3 w-3"
                      />
                      <span>Hide 0 rows{hideEmptyCards && hiddenCount > 0 ? ` (${hiddenCount})` : ''}</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleDeleteSelected}
                      disabled={selectedIds.size === 0}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent"
                      title="Delete all selected cards"
                    >
                      <Trash2 size={10} />
                      Delete ({selectedIds.size})
                    </button>
                  </div>
                  <ul className="space-y-1">
                    {visibleCards.map((c, i) => (
                      <li
                        key={c.id}
                        className={`p-1.5 rounded border text-[11px] ${activeCardId === c.id ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}
                      >
                        <div className="flex items-start gap-1">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(c.id)}
                            onChange={() => handleToggleSelect(c.id)}
                            className="mt-0.5 h-3 w-3"
                            aria-label={`Select ${c.label}`}
                          />
                          <button
                            type="button"
                            onClick={() => handleLoadCard(c)}
                            className="flex-1 text-left min-w-0"
                            title={`${c.label} — click to load into editor`}
                          >
                            <div className="font-medium text-gray-800 break-words whitespace-normal leading-tight">
                              <span className="text-gray-400 mr-1">{i + 1}.</span>{c.label}
                            </div>
                            <div className="text-[10px] text-gray-500 flex items-center gap-1 flex-wrap">
                              <span>{c.rowCount} rows</span>
                              <span className={`px-1 rounded ${c.source === 'auto' ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>
                                {c.source === 'auto' ? (c.edited ? 'AUTO · EDITED' : 'AUTO') : 'YOU'}
                              </span>
                              {c.kind === 'timeline' && (
                                <span className="px-1 rounded bg-amber-100 text-amber-700">timeline</span>
                              )}
                              {c.rowCount === 0 && !c.error && (
                                <span className="px-1 rounded bg-yellow-100 text-yellow-700">empty</span>
                              )}
                              {c.error && (
                                <span className="px-1 rounded bg-red-100 text-red-700">failed</span>
                              )}
                            </div>
                            {c.error && (
                              <div className="text-[10px] text-red-700 break-words whitespace-normal leading-tight mt-0.5">
                                {c.error}
                              </div>
                            )}
                            {c.rationale && (
                              <div className="text-[10px] text-gray-400 italic break-words whitespace-normal leading-tight mt-0.5">
                                {c.rationale}
                              </div>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteCard(c.id)}
                            className="text-gray-400 hover:text-red-600"
                            aria-label={`Delete ${c.label}`}
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {listedCards.length === 0 && (
                    <p className="text-[10px] italic text-gray-400">
                      All {cards.length} cards have 0 rows — untick "Hide 0 rows" to see them.
                    </p>
                  )}
                  {listedCards.length > 10 && (
                    <button
                      type="button"
                      onClick={() => setShowAllCards((v) => !v)}
                      className="text-[10px] text-indigo-600 hover:text-indigo-800 hover:underline self-start"
                    >
                      {showAllCards ? `Show top 10` : `Show all (${listedCards.length})`}
                    </button>
                  )}
                </>
              );
            })()}

            <div className="mt-auto pt-2 border-t border-gray-200 space-y-1">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block">
                Analyst notes
              </label>
              <textarea
                value={analystNotes}
                onChange={(e) => setAnalystNotes(e.target.value)}
                placeholder="Optional context for the LLM…"
                className="w-full text-[11px] border border-gray-300 rounded p-1.5 h-16 resize-y"
              />
              {sendError && (
                <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded p-1">
                  {sendError}
                </div>
              )}
              {onSend && (
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!canSend || sending}
                  className="w-full text-[11px] inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40"
                  title="Send ticket details + analyst-curated SQL evidence to the LLM"
                >
                  {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                  {sendLabel ?? 'Send ticket + SQL to Analysis'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Schema Browser modal — rendered at the section root so its
          fixed-position backdrop covers the whole viewport regardless
          of the SQL Investigation section's scroll position. */}
      <SchemaBrowserModal
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onAddTables={handleAddTablesFromBrowser}
      />
    </div>
  );
}

/**
 * Renders a single SqlCard with its label, source badge, SQL, and rows.
 * Used by EraxTab and TimelineTab so they mirror exactly what the analyst
 * sees in the SQL Investigation Saved Results pane.
 */
export function CardBlock({ card }: { card: SqlCard }) {
  // A card handed over from the ticket chat opens with its SQL visible —
  // the analyst was just reading that query and should not have to click
  // the chevron to see it again.
  const [sqlOpen, setSqlOpen] = useState(card.id.startsWith('chat-'));
  return (
    <div className="mt-1 p-2 bg-gray-50 rounded text-xs border border-gray-200">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-gray-800">{card.label}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
          card.source === 'auto' ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-700'
        }`}>
          {card.source === 'auto' ? (card.edited ? 'AUTO · EDITED' : 'AUTO') : 'YOU'}
        </span>
        <span className="text-[10px] text-gray-500">{card.rowCount} row{card.rowCount === 1 ? '' : 's'}</span>
        {card.rowCount === 0 && !card.error && (
          <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">empty</span>
        )}
        {card.error && (
          <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">failed</span>
        )}
      </div>
      {card.error && (
        <div className="mt-1 text-[10px] text-red-700 break-words">{card.error}</div>
      )}
      {card.rationale && (
        <div className="mt-1 text-[10px] text-gray-400 italic break-words">{card.rationale}</div>
      )}
      {card.sql && (
        <div className="mt-1.5">
          <button
            type="button"
            onClick={() => setSqlOpen((v) => !v)}
            aria-expanded={sqlOpen}
            className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 hover:text-blue-800"
          >
            {sqlOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            SQL Query
          </button>
          {sqlOpen && (
            <pre className="mt-1 p-2 bg-gray-900 text-green-300 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
              {card.sql}
            </pre>
          )}
        </div>
      )}
      {card.rows.length > 0 && (
        <div className="mt-1.5">
          <RowsTable rows={card.rows} />
        </div>
      )}
    </div>
  );
}

/** Minimal rows table; mirrors SimpleTable styling from IssueAnalysisPanel. */
function RowsTable({ rows, maxRows = 50 }: { rows: Record<string, unknown>[]; maxRows?: number }) {
  if (rows.length === 0) return null;
  const columns = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr className="border-b border-gray-300 text-left">
            {columns.map((c) => (
              <th key={c} className="py-1 px-1.5 font-semibold text-gray-600 bg-gray-100 whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, maxRows).map((row, i) => (
            <tr key={i} className="border-b border-gray-100 hover:bg-blue-50/30">
              {columns.map((c) => (
                <td key={c} className="py-0.5 px-1.5 text-gray-700 whitespace-nowrap max-w-[200px] truncate">
                  {row[c] == null ? <span className="text-gray-300 italic">null</span> : String(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > maxRows && (
        <p className="mt-0.5 text-[10px] text-gray-400 italic">Showing {maxRows} of {rows.length} rows</p>
      )}
    </div>
  );
}
